import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { formatCurrency, formatHour, getSupplyStr, formatDateLong, getReasonStr, formatDate2 } from '../../utils/lib';
import { useTranslation } from 'react-i18next';
import ListItemRectangle from '../../layouts/ListItemRectangle';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../../providers/ConfigProvider';
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PaymentsIcon from "@mui/icons-material/Payments";
import PersonIcon from "@mui/icons-material/Person";

function ViewPetInfo({ pet, onPostpone, onComplete, onViewDebtDetail, onAddPayment }) {
  const navigate = useNavigate();
  const [petStates, setPetStates] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { config } = useConfig();
  const { t } = useTranslation();

  useEffect(() => {
    setIsLoading(true);

      //pet states
    if (
      pet.states_pet.is_birthday || 
      pet.states_pet.supply != 'NA' || 
      pet.states_pet.appointments_tasks.length > 0 ||
      pet.states_pet.hasOwnProperty('last_visit') ||
      pet.death == 1 ||
      pet.waiting_room == 1 ||
      pet.states_pet.hasOwnProperty('last_food') ||
      pet.states_pet.hasOwnProperty('food_level') ||
      pet.age ||
      pet.states_pet.life_expectancy === 1
    )
      setPetStates(true);

    //owner debt
    if (pet.owners.some(owner => owner.balance > 0))
      setPetStates(true);

    setIsLoading(false);
  }, []);

  const handleOwnerClick = (owner) => {
    navigate('/main/view-owner', { state: { id: owner.id, updateLastView: false } });
  };

  if (!pet || isLoading) return (
    <>
      <CircularProgress
        size={42}
        sx={{ position: 'absolute', top: '50%', left: '50%', marginTop: '-21px', marginLeft: '-21px' }}
      />
    </>
  );

  return (
    <>
      {/* Pet states Section */}
      {petStates && (     
        <Box
          style={{
            marginBottom: '8px',
            marginTop: '16px',
            padding: '8px',
            background: '#fff',
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
            borderRadius: '8px',
            display: 'flex',
            gap: '8px',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          {pet.death === 1 &&
            <Typography variant="caption">
              {t('edit.decease')}
            </Typography>
          }
          {pet.waiting_room &&
            <Typography variant="caption">
              {t('reports.inWaitingRoom')}
            </Typography>
          }
          {pet.owners
            .filter(owner => owner.balance > 0)
            .map(owner => (
              <Box 
                key={owner.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%'
                }}
              >
                <Typography variant="caption">
                  {t('view.ownerDebt', { amount: formatCurrency(owner.balance) })}
                </Typography>
                <Box>
                  <Tooltip title={t('common.viewDetail')}>
                    <IconButton
                      size="small"
                      onClick={() => onViewDebtDetail?.(owner)}
                      sx={{ color: 'text.secondary', mr: 1 }}
                    >
                      <ReceiptIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('view.registerPayment')}>
                    <IconButton
                      size="small"
                      onClick={() => onAddPayment?.(owner)}
                      sx={{ color: 'text.secondary' }}
                    >
                      <PaymentsIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))
          }
          {pet.states_pet.appointments_tasks.map(agenda => (
            <Box 
              key={agenda.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%'
              }}
            >
              <Typography variant="caption">
                {t('view.petAgendaCaption', {
                  event: agenda.event_name,
                  time: formatHour(agenda.begin_time),
                })}
              </Typography>
              <Box>
                <Tooltip title={t('view.postponeTask')}>
                  <IconButton
                    size="small"
                    onClick={() => onPostpone(agenda)}
                    sx={{ color: 'text.secondary', mr: 1 }}
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('view.markAsDone')}>
                  <IconButton
                    size="small"
                    onClick={() => onComplete(agenda)}
                    sx={{ color: 'text.secondary' }}
                  >
                    <CheckIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}
          {pet.states_pet.supply != 'NA' &&
            <Typography variant="caption">
              {getSupplyStr(pet.states_pet.supply)}
            </Typography>
          }
          {pet.states_pet.is_birthday === 1 &&
            <Typography variant="caption">
              {t('view.petBirthdayToday')}
            </Typography>
          }
          {pet.states_pet.hasOwnProperty('last_visit') &&
            <Typography variant="caption">
              {t('view.petLastVisit', {
                date: formatDateLong(pet.states_pet.last_visit.date),
                reason: getReasonStr(pet.states_pet.last_visit.reason),
                user: pet.states_pet.last_visit.user_name,
              })}
            </Typography>
          }
          {pet.states_pet.hasOwnProperty('last_food') &&
            <Typography variant="caption">
              {t('view.petLastFood', {
                product: pet.states_pet.last_food.product_name,
                date: formatDate2(pet.states_pet.last_food.date),
              })}
            </Typography>
          }
          {pet.states_pet.hasOwnProperty('food_level') &&
            <Typography variant="caption">
              {t('view.petFoodLevel', { level: pet.states_pet.food_level })}
            </Typography>
          }
          {pet.age &&
            <Typography variant="caption">
              {t('view.petAge', { age: pet.age })}
            </Typography>
          }
          {pet.states_pet.life_expectancy === 1 &&
            <Typography variant="caption">
              {t('view.petLifeExpectancyExceeded')}
            </Typography>
          }
        </Box>
      )}

      {/* Owners Section */}
      <Box
        style={{
          marginBottom: '16px',
          padding: '8px',
          background: '#fff',
          boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
          minHeight: '110px',
        }}
      >
        <Typography variant="caption" style={{ margin: '8px' }}>
          {t('view.petOwnersTitle')}
        </Typography>
        <Box 
          style={{ 
            display: 'flex', 
            gap: '8px',
            overflowX: 'auto',
            paddingBottom: '8px',
            WebkitOverflowScrolling: 'touch',
            msOverflowStyle: '-ms-autohiding-scrollbar'
          }}
        >
          {pet.owners.map(owner => (
            <ListItemRectangle
              key={owner.id}
              id={owner.id}
              name={owner.name}
              thumb_url={owner.thumb_url}
              phone={owner.phone}
              onClick={() => handleOwnerClick(owner)}
              fallbackIcon={<PersonIcon />}
            />
          ))}
        </Box>
      </Box>

      {/* Info Section */}
      <Box style={{ marginBottom: '16px' }}>
        <Typography variant="caption">{t('common.name')}</Typography>
        <Typography
          variant="body1"
          style={{ fontSize: '16px' }}
        >
          {pet.name}
        </Typography>
      </Box>

      {/* Text fields with optional visibility */}
      {pet.race && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.race')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {pet.race.name}
          </Typography>
        </Box>
      )}
      
      {pet.pelage && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.pelage')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {pet.pelage.name}
          </Typography>
        </Box>
      )}

      {pet.gender && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.gender')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {pet.gender.name}
          </Typography>
        </Box>
      )}

      {pet.character && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.character')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {pet.character.name}
          </Typography>
        </Box>
      )}

      {pet.birthday && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.birthday')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {formatDate2(pet.birthday)}
          </Typography>
        </Box>
      )}

      {pet.weight && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{`${t('common.weight')} ${config?.vet?.unit_system === 'METRIC' ? ' (kg)' : ' (Lb)'}`}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {pet.weight}
          </Typography>
        </Box>
      )}

      {pet.chip && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.chip')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {pet.chip}
          </Typography>
        </Box>
      )}

      {pet.notes && (
        <Box style={{ marginBottom: '16px' }}>
          <Typography variant="caption">{t('common.notes')}</Typography>
          <Typography
            variant="body1"
            style={{ fontSize: '16px' }}
          >
            {pet.notes}
          </Typography>
        </Box>
      )}

    </>
  );
}

export default ViewPetInfo;

